import {
  type DetailedHTMLProps,
  forwardRef,
  type InputHTMLAttributes,
} from 'react';

import clsx from 'clsx';

import { cn } from '@quick/utils';

type Size = 'sm' | 'xs' | 'md';

const InputFilter = forwardRef<
  HTMLInputElement,
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
    label?: string;
    error?: string | false;
    innerClassName?: string;
    labelClassName?: string;
    /** @deprecated */
    isOnModal?: boolean;
    labelSize?: Size;
    requiredMark?: boolean;
  }
>(function InputFilter(
  {
    label,
    className,
    error,
    innerClassName,
    labelClassName,
    isOnModal,
    labelSize = 'xs',
    children,
    requiredMark,
    ...props
  },
  ref
) {
  return (
    <div
      className={clsx(
        'outline-brand relative flex flex-col rounded outline-1 focus-within:outline',
        className
      )}
    >
      {children}
      <input
        className={clsx(
          `dark:text-gray-light peer h-full w-full rounded border border-gray-200 p-2
          focus:outline-none`,
          'disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:text-gray-400',
          // isOnModal
          // PWA style has changed enough that this class should apply to all
          `bg-white dark:border-zinc-500 dark:bg-gray-600 dark:disabled:border-gray-600
          dark:disabled:bg-gray-700`,
          error &&
            `shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.300)]
            dark:shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.500)]`,
          innerClassName
        )}
        {...props}
        ref={ref}
      />
      {label && (
        <FloatingLabel
          isOnModal={isOnModal}
          size={labelSize}
          label={label}
          htmlFor={props.id}
          className={labelClassName}
          required={requiredMark}
        />
      )}
      {error && (
        <span
          className="pointer-events-none absolute left-2 top-[calc(100%-1rem)] text-xs font-semibold
            text-red-600"
        >
          {error}
        </span>
      )}
    </div>
  );
});

/**
 * @description Floating label, needs relative parent, use as span when inside a label
 */
export function FloatingLabel({
  size = 'xs',
  htmlFor,
  label,
  isOnModal,
  as = 'label',
  className,
  required,
}: {
  size?: Size;
  htmlFor?: string;
  label: string;
  /** @deprecated */
  isOnModal?: boolean;
  as?: 'label' | 'span';
  className?: string;
  required?: boolean;
}) {
  const Comp = as;
  return (
    <Comp
      htmlFor={htmlFor}
      className={cn(
        `absolute left-2 z-10 max-w-[calc(100%-1rem)] truncate font-semibold
        leading-normal text-gray-700 before:absolute before:top-1/2 before:-z-10
        before:h-0.5 before:w-full before:-translate-y-1/2 peer-disabled:bg-transparent
        peer-disabled:text-gray-500 dark:text-white dark:peer-disabled:text-gray-400`,
        size === 'xs' && '-top-2 text-xs',
        size === 'sm' && '-top-2.5 text-sm',
        size === 'md' && '-top-3 text-base',
        // isOnModal
        // PWA style has changed enough that this class should apply to all
        'before:bg-[#fbfbfb] dark:before:bg-gray-700',
        className
      )}
      title={label}
    >
      {required && (
        <span className="float-right text-red-500 dark:text-red-700">*</span>
      )}
      <span>{label}</span>
    </Comp>
  );
}

export { InputFilter };
